import { MessageDescriptor } from 'react-intl';

import { AssetType } from '@/shared/api/protocol-ts/model/dto_asset_pb';
import {
  CBCT_Anatomy,
  CBCT_Tooth_Anatomy,
} from '@/shared/api/protocol-ts/model/dto_common_anatomy_pb';
import { BBox } from '@/shared/api/protocol-ts/model/dto_common_geometry_pb';
import { OrganizationPermissions } from '@/shared/api/protocol-ts/model/dto_organization_permissions_pb';
import { PatientPermissions } from '@/shared/api/protocol-ts/model/dto_patient_permissions_pb';
import { ConditionCode } from '@/shared/api/protocol-ts/model/dto_report_condition_codes_pb';
import { ReportType } from '@/shared/api/protocol-ts/model/dto_report_pb';
import { ReportPermissions } from '@/shared/api/protocol-ts/model/dto_report_permissions_pb';
import {
  IOXRay_Anatomy,
  IOXRay_Tooth_Anatomy,
  Pano_Anatomy,
  Pano_Tooth_Anatomy,
} from '@/shared/api/protocol-ts/model/dto_report_type_2D_common_pb';
import { Report_CBCT_IOS_Superimposition_Orientation } from '@/shared/api/protocol-ts/model/dto_report_type_cbct_ios_superimposition_pb';
import { StudyType } from '@/shared/api/protocol-ts/model/dto_study_pb';
import { StudyPermissions } from '@/shared/api/protocol-ts/model/dto_study_permissions_pb';
import { UserPermissions } from '@/shared/api/protocol-ts/model/dto_user_permissions_pb';
import { Annotation } from '@/shared/graphics/RenderComponents/AnnotationsLayer/Annotation';

import { ToothWithDetections } from '@/entities/tooth';

import { PolygonMask } from '../graphics/RenderComponents/RCPolygonMask/RCPolygonMask';

export type UserLocale = string;

export type DayGroup<T> = {
  dayDate: Date;
  dayKey: number;
  items: T[];
};

export enum LocalStorageKey {
  theme = 'theme',
  view = 'view',
  organizationID = 'organizationID',
  signUpFormData = 'signUpFormData',
  UTMParameters = 'UTMParameters',
  demoSession = 'demoSession',
  unauthenticatedLocale = 'unauthenticatedLocale',
  preSignURL = 'preSignURL',
  presignMFAExempt = 'presignMFAExempt',
  fhmprToothPageSliceMode = 'fhmpr-slice-mode-tooth',
  fhmprVolumeSliceMode = 'fhmpr-slice-mode-volume',
  fhmprModalSliceMode = 'fhmpr-slice-mode-modal',
  fhmprPaneLayout = 'fhmpr-pane-layout',
  fhmprImageOptionsSync = 'fhmpr-image-options-sync',
  templateListNameColumnWidth = 'templateList.nameColumnWidth',
  patientListColumns = 'patientList.columns',
}

export type LoadingStateType = 'idle' | 'pending' | 'failed' | 'succeeded';

export type SortingType = 'default' | 'asc' | 'desc';

export type MPRInputMode = 'normal' | 'brightness' | 'pan' | 'zoom' | 'depth';

export type PrimitiveType = string | number | boolean | null | undefined;

export type DefaultInitialState = {
  loading: LoadingStateType;
};

export enum StreamDataAccumulatorKey {
  study = 'study',
  reports = 'reports',
  teeth = 'teeth',
  conditions = 'conditions',
  conditionsTeethGroup = 'conditionsTeehGroup',
  conditionsMaxFaxGroup = 'conditionsMaxFaxGroup',
  assets = 'assets',
  patientProfileGuardedAssetsIDs = 'patientProfileGuardedAssetsIDs',
  access = 'access',
  /**
   * @deprecated
   */
  initialROITeethISONumbers = 'initialROITeethISONumbers',
  toothLandmarks = 'toothLandmarks',
  initialROITeethIDs = 'initialROITeethIDs',
  dentalPhotoAlbum = 'dentalPhotoAlbum',
  organizationStaff = 'organizationStaff',
}

export enum AccountInfoStreamDataAccumulatorKeys {
  inventory = 'inventory',
  orders = 'orders',
  diagnocoinBatches = 'diagnocoinBatches',
}

export type Mask = {
  url: string;
  id: string;
};

export type MedicalImageInterface = {
  id: string;
  src: string;
  assetID: string;
  image?: {
    Height: number;
    Width: number;
  };
  annotations?: Annotation[];
  strideMm?: number;
  thicknessMm?: number | string;
  masks?: PolygonMask[];
  path?: string;
  localizationID?: string;
  rightSideOrient?: string;
  leftSideOrient?: string;
  BBox?: BBox;
  viewOptions?: Partial<{
    sharpness: number;
    invert: boolean;
    brightness: number;
    contrast: number;
    wwwc: { ww: number; wc: number };
  }>;
  groupName?: string | number;
  order?: number;
  kind?: 'dicom' | 'raster';
  hasScale?: boolean;
};

export type ToolNames =
  | CombinedToolNames
  | ToggledToolNames
  | StatelessToolNames;

export const combinedToolNames = [
  'invert',
  'split',
  'expand',
  'hideMarks',
  'editNumbers',
] as const;
export type CombinedToolNames = (typeof combinedToolNames)[number];

export const statelessToolNames = ['reset', 'download', 'ios', 'turn'] as const;
export type StatelessToolNames = (typeof statelessToolNames)[number];

export const toggledToolNames = [
  'ruler',
  'arrow',
  'angle',
  'eraser',
  'view',
  'sharpening',
  'brightness',
  'move',
  'zoom',
  'sharpness',
  'editMask',
  'polyline',
] as const;
export type ToggledToolNames = (typeof toggledToolNames)[number];

export type LocationStateType = {
  lastPositionPatientProfile: number;
  lastPositionReport: number;
  recentReportIndex: number;
  toothID: string;
  lastOpenTabStudyID: string;
};

export type SortByType = {
  patientName?: boolean;
  clinicName?: boolean;
  senderName?: boolean;
  senderEmail?: boolean;
  sharingDate?: boolean;
  status?: boolean;
  recipientEmail?: boolean;
  dateOfBirth?: boolean;
};

export type SortKeyType = {
  [key in PatientsListColumnName]: SortByType;
};

export type SortByFunctionType = (
  sortKey: keyof SortKeyType,
  leastOrGreatest: boolean,
) => void;

export enum PatientsListColumnName {
  ClinicName = 'Clinic name',
  SharedBy = 'Shared by',
  SenderEmail = 'Sender Email',
  SharedWith = 'Shared with',
  SharingDate = 'Sharing date',
  Status = 'Status',
  PatientName = 'PatientName',
  PatientID = 'PatientID',
  DateOfBirth = 'DateOfBirth',
  Doctors = 'Doctors',
  LastModified = 'LastModified',
  Studies = 'Studies',
  Delete = 'Delete',
  Default = 'Default',
}

export type SelectOptionType = {
  value: string;
  label: string;
};

export type BreadcrumbElementType = {
  label: string | JSX.Element;
  path: string;
  state?: LocationStateType;
};

export type FormatMessageFunction = (
  messageDescriptor: MessageDescriptor,
  values?: Record<string, PrimitiveType>,
) => string;

export type PatientPermissionsKeys = keyof PatientPermissions;
export type ReportPermissionsKeys = keyof ReportPermissions;
export type OrganizationPermissionsKeys = keyof OrganizationPermissions;
export type UserPermissionsKeys = keyof UserPermissions;
export type StudyPermissionsKeys = keyof StudyPermissions;

export type RenderPreviewSettings = {
  isPreview: boolean;
  isInverted: boolean;
  isBW: boolean;
  showMasks?: boolean;
  showPanoramaSplit?: boolean;
};

export type OrderReportType =
  | 'CBCT'
  | 'Pano'
  | 'IOXRay'
  | '3DStudio'
  | 'ImplantStudio'
  | 'OrthoStudio'
  | 'IOSSegmentation'
  | 'RawMeshViewer'
  | 'Transcriptor';

export type OrderReportActionParams = {
  mainStudyID: string;
  IOSMeshesStudyID?: string;
  SecondaryCBCTStudyID?: string;
  superimpositionOrientation?: Report_CBCT_IOS_Superimposition_Orientation;
  dentalPhotoStudyIDs: string[];
};

export type MaskGroup = 'perio' | 'restorative' | 'endo' | 'chart';
export type MaskFilterParams = Record<
  'isActive' | 'disabled' | 'hide',
  boolean
>;
export type MaskFilter = Record<MaskGroup, MaskFilterParams>;

export type Mask2DConfigCodes = {
  conditionCode?: ConditionCode;
  IOXRayToothAnatomy?: IOXRay_Tooth_Anatomy;
  PanoToothAnatomy?: Pano_Tooth_Anatomy;
  CBCTToothAnatomy?: CBCT_Tooth_Anatomy;
  IOXRayMaxFaxAnatomy?: IOXRay_Anatomy;
  PanoMaxFaxAnatomy?: Pano_Anatomy;
  CBCTMaxFaxAnatomy?: CBCT_Anatomy;
};

export type PendingChange = {
  tooth: ToothWithDetections;
  newNumber: number;
  SupernumeraryIndex: number;
};

export type CropBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type ImageSizeType = 'original' | 'thumbnail' | 'preview' | 'optimized';

export type UploadStudyType =
  | AssetType.AssetType_Study_CBCT
  | AssetType.AssetType_Document
  | AssetType.AssetType_Study_IntraoralXRay
  | AssetType.AssetType_Study_DentalPhoto
  | AssetType.AssetType_Study_PanoramicXRay
  | AssetType.AssetType_Study_IOS_Meshes;

export type AcceptedFileTypesConfig = Record<string, string[]>;

export type StudyMetaInfo = {
  ID: string;
  studyType: StudyType;
  // Date of study / Upload date
  uploadDate?: Date;
  // NOTE: Only CBCT meta
  studyTime?: Date;
  scanUID?: string;
  manufacturer?: string;
  model?: string;
  exposure?: string;
  fov?: string;
  voxel?: string;
};

export type AnalyticsEventProperties = {
  cluster?: string;
  patient_id?: string;
  report_id?: string;
  report_type?: ReportType;
  tooth_number?: number;
  filter?: string;
  tog_status?: string;
  pane_layout?: string;
  sorting_state?: string;
  logout_reason?: string;
  tab?: string;
  error_no?: string;
  user_id?: string;
  email?: string;
  organization_id?: string;
  photo_id?: string;
  album_id?: string;
  language?: string;
  languages?: string;
  kb_slug?: string;
  kb_market?: string;
  kb_title?: string;
  consultation_type?: string;
  schema_id?: string;
  period?: string;
};

export type AnalyticsEventName =
  | 'Login Success'
  | 'Logout Success'
  | 'Logout ForeignInvitation'
  | 'LogoutTimeout'
  | 'AdditionalInformation Page Viewed'
  | 'CompanySettings Page Viewed'
  | 'ConfirmEmail Page Viewed'
  | 'ConfirmSharing Page Viewed'
  | 'ErrorPage Page Viewed'
  | 'FinishRegistration Page Viewed'
  | 'GpReport Page Viewed'
  | 'InviteSignUp Page Viewed'
  | 'IOXRayReport Page Viewed'
  | 'NewPassword Page Viewed'
  | 'OrganizationInvite Page Viewed'
  | 'PanoReport Page Viewed'
  | 'PanowingsReport Page Viewed'
  | 'PatientProfile Page Viewed'
  | 'Patients Page Viewed'
  | 'PersonalSettings Page Viewed'
  | 'PreviewReport Page Viewed'
  | 'ResetPassword Page Viewed'
  | 'Signin Page Viewed'
  | 'SignUp Page Viewed'
  | 'Tooth Page Viewed'
  | 'PatientsPageViewToggleClicked'
  | 'PatientProfilePageCBCTOrderReportButtonClicked'
  | 'PatientProfilePagePanoOrderReportButtonClicked'
  | 'PatientProfilePageIOXRayAIOrderReportButtonClicked'
  | 'PatientProfilePage3DModelOrderReportButtonClicked'
  | 'PatientProfilePageOrthoStudioOrderReportButtonClicked'
  | 'PatientProfilePageImplantStudioOrderReportButtonClicked'
  | 'PatientProfilePageRecordingsWidgetClicked'
  | 'PatientProfilePageRecordingsWidgetItemClicked'
  | 'PatientProfilePageTimelineRecordingClicked'
  | 'PatientProfilePageCompareStudiesClicked'
  | 'PatientRecordingsPageViewed'
  | 'PatientRecordingsPageRecordingClicked'
  | 'PatientRecordingsPageStartRecordingButtonClicked'
  | 'ReportPageSignReportButtonClicked'
  | 'ReportPageToothApproveButtonClicked'
  | 'ReportPageFooterApproveAndSignButtonClicked'
  | 'ReportPageToothSlicesButtonClicked'
  | 'ReportPageSelectTeethButtonClicked'
  | 'ReportPageFooterSuspiciousTeethToggleClicked'
  | 'ReportPageFooterConditionDetailsToggleClicked'
  | 'ReportPageAddConditionsButtonClicked'
  | 'ReportPageConditionsPopUpCloseButtonClicked'
  | 'ReportPageToothFormulaButtonClicked'
  | 'ToothPageToothFormulaButtonClicked'
  | 'ToothPageMPRAxesButtonClicked'
  | 'ToothPageMPRAxesToggleClicked'
  | 'ToothPageMPRBrightnessContrastButtonClicked'
  | 'ToothPageMPRPanButtonClicked'
  | 'ToothPageMPRZoomButtonClicked'
  | 'ToothPageMPRRotateButtonClicked'
  | 'ToothPageMPRRotateAxesButtonClicked'
  | 'ToothPageMPRScrollButtonClicked'
  | 'ToothPageMPRResetButtonClicked'
  | 'ToothPageMPRZoomSyncToggleClicked'
  | 'ToothPageMPRBrightnessContrastSyncToggleClicked'
  | 'ToothPageMPRPaneLayoutSelected'
  | 'ReportPageMPRAxesButtonClicked'
  | 'ReportPageMPRAxesToggleClicked'
  | 'ReportPageMPRBrightnessContrastButtonClicked'
  | 'ReportPageMPRPanButtonClicked'
  | 'ReportPageMPRZoomButtonClicked'
  | 'ReportPageMPRRotateButtonClicked'
  | 'ReportPageMPRRotateAxesButtonClicked'
  | 'ReportPageMPRScrollButtonClicked'
  | 'ReportPageMPRResetButtonClicked'
  | 'ReportPageMPRZoomSyncToggleClicked'
  | 'ReportPageMPRBrightnessContrastSyncToggleClicked'
  | 'ReportPageMPRPaneLayoutSelected'
  | 'ToothPageUploadedSlicesButtonClicked'
  | 'ToothPageToothCardApproveButtonClicked'
  | 'ToothPageMainApproveButtonClicked'
  | 'ToothPageAddConditionsButtonClicked'
  | 'ToothPageConditionPopUpCloseButtonClicked'
  | 'DentalPhotosPageViewed'
  | 'PatientsPageReportWidgetClicked'
  | 'PatientsPageDentalPhotosWidgetClicked'
  | 'PatientsPagePatientNameSortingClicked'
  | 'PatientsPageDOBFilterApplyButtonClicked'
  | 'PatientsPageTreatingDoctorsFilterApplyButtonClicked'
  | 'PatientsPageStudiesFilterApplyButtonClicked'
  | 'PatientsPageReportsFilterApplyButtonClicked'
  | 'PatientsPageLastModifiedFilterApplyButtonClicked'
  | 'PatientsPageReceivedReferralsClicked'
  | 'PatientsPageSentReferralsClicked'
  | 'ReportPagePanoramaExpandButtonClicked'
  | 'ReportPagePanoramaSplitButtonClicked'
  | 'ReportPagePanoramaBrightnessContrastButtonClicked'
  | 'ReportPagePanoramaSharpnessButtonClicked'
  | 'ReportPagePanoramaInvertButtonClicked'
  | 'ReportPagePanoramaResetButtonClicked'
  | 'ReportPagePanoramaDownloadButtonClicked'
  | 'ReportPagePanoramaPerioButtonClicked'
  | 'ReportPagePanoramaRestorativeButtonClicked'
  | 'ReportPagePanoramaEndoButtonClicked'
  | 'ReportPagePanoramaAnatomyButtonClicked'
  | 'DentalPhotosUploadButtonClicked'
  | 'DentalPhotosAddPhotoToAlbumClicked'
  | 'DentalPhotosPhotoClicked'
  | 'DentalPhotosAlbumDownloadClicked'
  | 'DentalPhotosAlbumRemoveClicked'
  | 'DentalPhotosChangePhotoPlaceInAlbumDragDrop'
  | 'DentalPhotosOpenedPhotoDownloadClicked'
  | 'DentalPhotosOpenedPhotoRemoveClicked'
  | 'AllPagesKnowledgeBaseIconClicked'
  | 'AllPagesLanguageChangeClicked'
  | 'AllPagesDarkThemeClicked'
  | 'AllPagesLightThemeClicked'
  | 'ReportPageClinicalCaseDescriptionSaveClicked'
  | 'ReportPagePrintReportWithoutSignatureButtonClicked'
  | 'ReportPagePrintReportButtonClicked'
  | 'PreviewReportPagePrintButtonClicked'
  | 'PreviewReportPageDownloadPDFButtonClicked'
  | 'PreviewReportPageB&WSettingButtonClicked'
  | 'PreviewReportPageColorSettingButtonClicked'
  | 'PreviewReportPageInvertedCheckboxClicked'
  | 'PreviewReportPageProbabilityCheckboxClicked'
  | 'PreviewReportPageToothChartCheckboxClicked'
  | 'PreviewReportPageUpperJawCheckboxClicked'
  | 'PreviewReportPageLowerJawCheckboxClicked'
  | 'PreviewReportPageConditionsCheckboxClicked'
  | 'PreviewReportPageSlicesCheckboxClicked'
  | 'PreviewReportPageShowMasksCheckboxClicked'
  | 'PreviewReportPageOtherFindingsCheckboxClicked'
  | 'PreviewReportPageStudyInfoCheckboxClicked'
  | 'TranscriptorReportPage Viewed'
  | 'PatientProfilePageDentalPhotoOrderReportButtonClicked'
  | 'PatientProfilePageNewDocumentButtonClicked'
  | 'PatientProfilePageTranscriptorOrderReportButtonClicked'
  | 'PatientProfilePageTranscriptorReorderButtonClicked'
  | 'TranscriptorReportPageReorderButtonClicked'
  | 'TranscriptorReportPageAddDocumentButtonClicked'
  | 'PatientProfilePageTranscriptorOrderModalStartRecordingButtonClicked'
  | 'PatientProfilePageTranscriptorOrderModalUploadStarted'
  | 'PatientProfilePageTranscriptorRecordModalPauseButtonClicked'
  | 'PatientProfilePageTranscriptorRecordModalResumeButtonClicked'
  | 'PatientProfilePageTranscriptorRecordModalFinishButtonClicked'
  | 'TranscriptorReportPageEvaluationTabClicked'
  | 'TranscriptorReportPageFollowupTabClicked'
  | 'TranscriptorReportPageSummaryTabClicked'
  | 'TranscriptorReportPageSummaryEditButtonClicked'
  | 'TranscriptorReportPageSummaryCopyButtonClicked'
  | 'TranscriptorReportPageFollowUpEditButtonClicked'
  | 'TranscriptorReportPageFollowUpCopyButtonClicked'
  | 'TranscriptorReportPageRegenerateButtonClicked'
  | 'PatientProfilePageTranscriptorWidgetRemoveButtonClicked'
  | 'PatientProfilePageViewHistoricalDataButtonClicked'
  | 'KnowledgeBasePanelOpened'
  | 'KnowledgeBaseArticleOpened';

export type AnalyticsData = {
  patientID?: string;
  reportID?: string;
  reportType?: ReportType;
  userID?: string;
  email?: string;
  organizationID?: string;
};

export type CountryData = {
  names: Record<string, string>;
  sign_up: boolean;
};

export type CountriesData = Record<string, CountryData>;

export type DateRangePeriod = 'all' | 'today' | 'week' | 'custom';

export type DateRange = {
  from: Date | undefined;
  to: Date | undefined;
};

export type InfoBannerVariant = 'sales' | 'info' | 'fatal' | 'warning';

export type InfoBannerClosableProps =
  | {
      closable?: true;
      storageKey: string;
    }
  | {
      closable: false;
      storageKey?: never;
    };

export type TemplatesEditor =
  | 'schema'
  | 'document'
  | 'analytics'
  | 'allDocuments';

export type TemplatesEditorMode = 'new' | 'edit' | 'view';

export type BuildTemplatesUrlParams = {
  specialty?: string;
  editor?: TemplatesEditor;
  mode?: TemplatesEditorMode;
  id?: string;
  importedTemplateSeriesId?: string;
};

export type PasswordValidationContext = {
  passwordMinLength?: number;
};
